import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {isThreadOutdated} from '../../../helpers/thread-navigation'
import {getFirstComment} from '../../../page-data/payloads/thread-previews'
import type {ConversationItem} from './build-conversation-items'
import type {ParsedCommentsFilterKeywords} from './parse-filter-keywords'
import type {CommentsFilterState} from '../use-comments-filter-state'

export type ParsedCommentsFilterState = Omit<CommentsFilterState, 'filterText'> & {
  parsedKeywords?: ParsedCommentsFilterKeywords
}

/**
 * Data on each item that is used to determine if it matches the current filter
 */
interface ItemFilterData {
  authorLogin: string
  body: string
  createdAt: string
  id: string
  isOutdated: boolean
  isResolved: boolean
  path: string
  comments: Comment[]
}

function getThreadFilterData(thread: Thread): ItemFilterData {
  const firstComment = getFirstComment(thread)
  const filePath = thread.path ?? ''

  return {
    authorLogin: firstComment?.author?.login ?? '',
    body: firstComment?.body ?? '',
    createdAt: firstComment?.createdAt ?? '',
    id: thread.id,
    isResolved: thread.isResolved ?? false,
    isOutdated: isThreadOutdated(thread),
    path: filePath,
    comments: thread.commentsData?.comments ?? [],
  }
}

function getConversationItemFilterData(item: ConversationItem): ItemFilterData {
  switch (item.type) {
    case 'thread':
      return getThreadFilterData(item.data)
    case 'issue-comment':
      return {
        authorLogin: item.data.authorLogin,
        body: item.data.body,
        createdAt: item.data.createdAt,
        id: item.data.id,
        isResolved: false, // issue comments don't have resolved state
        isOutdated: false, // issue comments don't have outdated state
        path: '', // issue comments don't have a file path
        comments: [], // issue comments don't have thread comments
      }
    case 'review-summary': {
      // FIXME: filter threads inside of reviews even when we don't filter the reviews themselves
      return {
        authorLogin: item.data.summary.authorLogin,
        body: item.data.summary.body,
        createdAt: item.data.summary.submittedAt,
        id: item.data.summary.id,
        isResolved: false,
        isOutdated: false,
        path: '',
        comments: [],
      }
    }
  }
}

/**
 * Return a set of thread ids that match the current filter state
 */
export function filterThreads(threadPreviews: Thread[], filterState: ParsedCommentsFilterState) {
  return threadPreviews.filter(thread => filterItem(getThreadFilterData(thread), filterState))
}

export function filterConversationItems(items: ConversationItem[], filterState: ParsedCommentsFilterState) {
  return items.filter(item => filterItem(getConversationItemFilterData(item), filterState))
}

function filterItem(data: ItemFilterData, filterState: ParsedCommentsFilterState) {
  const {parsedKeywords, hideResolvedThreads, hideOutdatedThreads} = filterState

  if (parsedKeywords) {
    const {afterDate, isResolved, remainingText} = parsedKeywords

    // When is:resolved or is:unresolved is in the filter text, use that to filter
    if (isResolved !== null) {
      if (data.isResolved !== isResolved) {
        return false
      }
    } else if (hideResolvedThreads && data.isResolved) {
      return false
    }

    if (hideOutdatedThreads && data.isOutdated) {
      return false
    }

    if (afterDate && !isAfterDate(data.createdAt, afterDate)) {
      return false
    }

    if (remainingText) {
      const filterTextLower = remainingText.toLowerCase()
      if (
        !data.authorLogin.toLowerCase().includes(filterTextLower) &&
        !data.body.toLowerCase().includes(filterTextLower) &&
        !data.path.toLowerCase().includes(filterTextLower)
      ) {
        return false
      }
    }
  } else {
    if (hideResolvedThreads && data.isResolved) {
      return false
    }
    if (hideOutdatedThreads && data.isOutdated) {
      return false
    }
  }

  if (filterState.selectedAuthor && data.authorLogin !== filterState.selectedAuthor) {
    return false
  }

  return true
}

function isAfterDate(createdAt: string, afterDate: Date): boolean {
  const commentDate = new Date(createdAt)
  return !isNaN(commentDate.getTime()) && commentDate > afterDate
}
